import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../api/api';
import { TimeSearchRow, TimeUser, Activity, AssignmentPreviewDto } from '../../../../../api/generated/data-contracts';
import { UseAssignmentPreviewReturn } from '../types';

interface UseAssignmentPreviewParams {
    selectedUser: TimeUser | null;
    selectedTask: TimeSearchRow | null;
    date: Date | null;
    isOpen: boolean;
}

export const useAssignmentPreview = ({ selectedUser, selectedTask, date, isOpen }: UseAssignmentPreviewParams): UseAssignmentPreviewReturn => {
    const [assignmentInfo, setAssignmentInfo] = useState<AssignmentPreviewDto | null>(null);
    const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
    const [customRate, setCustomRate] = useState<string>('');
    const [activities, setActivities] = useState<Activity[]>([]);

    // Fetch activities when modal opens
    useEffect(() => {
        if (isOpen) {
            ApiCalls.configurationActivitiesList()
                .then(response => setActivities(response.data || []))
                .catch(error => console.error('Failed to fetch activities:', error));
        }
    }, [isOpen]);

    // Preview assignment when user and task are selected
    const previewAssignmentMutation = useMutation({
        mutationFn: (request: { userId: string; taskId: string; date?: string }) => ApiCalls.timeEntryCorrectionsPreviewAssignmentCreate(request),
        onSuccess: response => {
            setAssignmentInfo(response.data);
        },
        onError: error => {
            console.error('Failed to preview assignment:', error);
            setAssignmentInfo(null);
        },
    });

    // Trigger assignment preview when user, task, or date are selected
    useEffect(() => {
        if (selectedUser && selectedTask) {
            previewAssignmentMutation.mutate({
                userId: selectedUser.id,
                taskId: selectedTask.taskId,
                date: date?.removeTimeZone().toISOString(),
            });
        } else {
            setAssignmentInfo(null);
            setSelectedActivity(null);
            setCustomRate('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUser, selectedTask, date]);

    // Pre-select activity or custom rate from existing approval when assignment info loads
    useEffect(() => {
        if (!assignmentInfo) return;

        // Pre-select activity if exists (always set, don't check userModifiedActivity since it will be locked)
        if (assignmentInfo.existingApprovalActivityId) {
            const activity = activities.find(a => a.id === assignmentInfo.existingApprovalActivityId);
            if (activity) {
                setSelectedActivity(activity);
            }
            // Clear custom rate when there's an existing activity
            setCustomRate('');
        } else {
            // Clear activity if no existing approval activity
            setSelectedActivity(null);
        }

        // Pre-fill custom rate if exists (always set, don't check userModifiedRate since it will be locked)
        if (assignmentInfo.existingApprovalCustomRate && !assignmentInfo.existingApprovalActivityId) {
            setCustomRate(assignmentInfo.existingApprovalCustomRate.toString());
        } else if (!assignmentInfo.existingApprovalCustomRate && !assignmentInfo.existingApprovalActivityId) {
            // Clear custom rate if no existing approval custom rate
            setCustomRate('');
        }
    }, [assignmentInfo, activities]);

    return {
        assignmentInfo,
        isLoadingPreview: previewAssignmentMutation.isPending,
        activities,
        selectedActivity,
        setSelectedActivity,
        customRate,
        setCustomRate,
    };
};
